import styled from "@emotion/styled";
import * as Dialog from "@radix-ui/react-dialog";
import { keyframes } from "@emotion/react";

const slideInFromLeft = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;

// Full-screen slide-in panel OVER EVERYTHING (sidebar + chat)
// Extends Dialog.Content so it creates its own focus scope,
// preventing the parent sidebar Dialog from stealing focus.
export const NotesSheet = styled(Dialog.Content)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: var(--keyboard-height, 0px);
    @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
    z-index: 2000; /* above the sidebar (1001) and rest of UI */
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: linear-gradient(180deg, #222327 0%, #1a1a1c 100%);

    &[data-state='open'] {
        pointer-events: auto;
        animation: ${slideInFromLeft} 0.3s ease-out forwards;
    }

    &[data-state='closed'] {
        pointer-events: none;
        animation: ${slideOutToLeft} 0.3s ease-out forwards;
    }
`;

// Scrollable body under the header
export const NotesBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

// Use full-width content
export const NotesMain = styled.div`
    padding: 0 24px 24px;
    width: 100%;
`;

export const NotesInner = styled.div`
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

// Back button row
export const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

// Meta row: date + dots
export const MetaRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;
`;

export const NoteDateText = styled.div`
    font-family: Inter, system-ui, sans-serif;
    font-weight: 500;
    font-style: normal;
    font-size: 14px;
    line-height: 120%;
    letter-spacing: 0;
    text-align: right;
    vertical-align: middle;
    color: #b7bdcb;
`;

// Three-dots button
export const DotsButton = styled.button`
    margin-left: auto;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    border: none;
    background: #26272d;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

export const DotsInner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px; /* equal spacing between dots */
`;

export const Dot = styled.span`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #b7bdcb;
`;

// Content area (acts like a flat textarea)
export const NoteContent = styled.div`
    margin-top: 16px;
    padding: 12px;
    border-radius: 12px;
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

// Single editor: first line styled as title via ::first-line
export const NoteEditor = styled.div`
    width: 100%;
    min-height: 160px;
    border: none;
    outline: none;
    background: transparent;
    resize: none;
    -webkit-user-select: text;
    user-select: text;
    -webkit-touch-callout: default;

    /* Base text styles (body) */
    font-family: Inter, system-ui, sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 160%;
    letter-spacing: 0;
    vertical-align: middle;
    color: #e9ecf5;

    white-space: pre-wrap;
    word-wrap: break-word;

    /* First line behaves like the title */
    &::first-line {
        font-weight: 700;
        font-size: 18px;
        line-height: 160%;
    }

    /* Placeholder when empty */
    &:empty::before {
        content: attr(data-placeholder);
        color: #4c4f5a;
        pointer-events: none;
    }

    a {
        color: #5E84ED;
        text-decoration: underline;
        word-break: break-all;
    }
`;

export const NoteErrorBanner = styled.div`
    padding: 12px 16px;
    border-radius: 10px;
    background: rgba(214, 95, 70, 0.12);
    border: 1px solid rgba(214, 95, 70, 0.3);

    font-family: Inter, system-ui, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
    color: #d65f46;
    text-align: center;
`;
